import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { VIRTUALIZE_THRESHOLD } from '../../lib/board-scale.constants';
import { STICKY_EDGE_SHADOW } from '../../lib/sticky-edge';
import { useHasEnabledAutomations } from '../../lib/useHasEnabledAutomations';
import { TrackersBriefRow, type BoardRowItem } from '../TrackersBriefRow';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@monksflow/monks-ui';
import {
  ArrowDown,
  ArrowDownUp,
  ArrowUp,
  ChevronDownIcon,
  ChevronRightIcon,
  Copy,
  GripVerticalIcon,
  MoreVertical,
  Pencil,
  PlusIcon,
  Settings,
  Trash2,
} from 'lucide-react';

import {
  COMMENTS_COLUMN_WIDTH,
  CUSTOM_COLUMN_DEFAULT_WIDTH,
  ITEM_COLUMN_MIN_WIDTH,
  type CustomColumn,
  type CustomColumnType,
  type ScrollContainersRef,
  type SyncedScrollLeftRef,
} from '../../lib/useTrackersBoard';
import { isSortableColumnType, type ColumnSort, type SortDir } from '../../lib/sorting';
import { AddColumnModal } from '../AddColumnModal';
import { useCanEditTracker, useCanManageTrackerColumns } from '../../lib/tracker-access';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

// Group accent colors. The group card is white; this color is used only for the
// group title text, the header's left edge, and each row's left stripe — so the
// hues can be vivid/colorful yet stay readable as text on white (all ≥ ~4.5:1).
// Exported so the Kanban/Timeline views color-code projects consistently.
export const GROUP_COLORS = [
  '#2563eb', // blue
  '#0f766e', // teal
  '#be185d', // rose
  '#7c3aed', // violet
  '#c2410c', // orange
  '#15803d', // green
];

/**
 * Which of a column header's reshaping actions this column honours.
 *
 * An action a board would drop on the floor is not rendered rather than
 * rendered and ignored: a record board's platform fields have no entry in the
 * layout's own column list, so Duplicate, Delete and Configure would all find
 * nothing and close the menu having done nothing.
 */
export interface ColumnMenuActions {
  rename: boolean;
  duplicate: boolean;
  remove: boolean;
  configure: boolean;
}

/** What a column the board itself owns can do, which is everything. */
const FULL_COLUMN_MENU: ColumnMenuActions = {
  rename: true,
  duplicate: true,
  remove: true,
  configure: true,
};

/** Column types whose header kebab offers a Configure item, and its wording. */
const CONFIGURE_LABEL_KEY: Partial<Record<CustomColumnType, ParseKeys<'trackers'>>> = {
  status: 'trackersBoard.trackersProjectGroup.configureStatus',
  button: 'trackersBoard.trackersProjectGroup.configureButton',
  formula: 'trackersBoard.trackersProjectGroup.configureFormula',
};

/**
 * Column-resize grip on a header's right edge. The table draws no vertical
 * borders, so a faint tick marks the edge while the column is hovered; the
 * grip itself turns primary under the pointer.
 */
function ResizeGrip({ onMouseDown }: { onMouseDown: (e: React.MouseEvent) => void }) {
  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- pointer-only column-resize grip; no keyboard equivalent
    <div
      className="absolute -right-[2px] top-0 z-20 flex h-full w-[4px] cursor-col-resize items-center justify-center transition-colors hover:bg-primary"
      onMouseDown={onMouseDown}
    >
      <span className="h-4 w-px bg-border opacity-0 transition-opacity group-hover/col:opacity-100" />
    </div>
  );
}

function SortableColumnHead({
  id,
  label,
  width,
  minWidth,
  configureLabelKey,
  onSetWidth,
  onRename,
  onAddColumnAfter,
  onDuplicate,
  onDelete,
  onConfigure,
  menu = FULL_COLUMN_MENU,
  sortable = false,
  sortDir = null,
  onToggleSort,
  readOnly = false,
  manageable = true,
}: {
  id: string;
  label: string;
  width: number | undefined;
  minWidth: number;
  /** Set for the types that have a config dialog; absent hides the menu item. */
  configureLabelKey: ParseKeys<'trackers'> | undefined;
  onSetWidth: (id: string, width: number) => void;
  onRename: (id: string, nextLabel: string) => void;
  onAddColumnAfter: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDelete: (id: string) => void;
  onConfigure: (id: string) => void;
  /** Which kebab actions this column honours; the rest are not offered. */
  menu?: ColumnMenuActions;
  /** Click-to-sort affordance: shown on hover, solid when this column sorts. */
  sortable?: boolean;
  sortDir?: SortDir | null;
  onToggleSort?: (id: string) => void;
  /** Sort only: no reorder, resize, rename or column menu. */
  readOnly?: boolean;
  /** False keeps reorder and resize but drops rename and the column menu. */
  manageable?: boolean;
}) {
  const { t } = useTranslation('trackers');
  const canRename = !readOnly && manageable && menu.rename;

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: readOnly,
  });
  const cellRef = useRef<HTMLTableCellElement | null>(null);

  const mergedRef = useCallback(
    (node: HTMLTableCellElement | null) => {
      setNodeRef(node);
      cellRef.current = node;
    },
    [setNodeRef],
  );

  const style: React.CSSProperties = {
    // `Translate.toString` emits only translate3d(...) — no scaleX/scaleY,
    // so the dragged element keeps its original width/height instead of
    // getting squashed or stretched by dnd-kit's default transform.
    transform: CSS.Translate.toString(transform),
    transition,
    // Always a CONCRETE width (never `undefined`). With table-layout:fixed, a
    // header cell with no width becomes a flexible/auto column that shares
    // leftover space, so resizing one column would visibly reflow the others.
    // Fixing every column's width leaves only the trailing spacer flexible.
    width: `${width ?? Math.max(minWidth, CUSTOM_COLUMN_DEFAULT_WIDTH)}px`,
    minWidth: `${minWidth}px`,
  };

  // Inline-rename state. Double-click the label → input. Blur/Enter commits,
  // Esc cancels. Drag listeners are detached while editing so the user can
  // select text without starting a column reorder.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(label);
  }, [label, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const commitRename = () => {
    if (draft.trim() === '') {
      setDraft(label);
      setEditing(false);
      return;
    }
    if (draft.trim() !== label) onRename(id, draft.trim());
    setEditing(false);
  };

  const cancelRename = () => {
    setDraft(label);
    setEditing(false);
  };

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = cellRef.current?.getBoundingClientRect().width ?? width ?? minWidth;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX;
      onSetWidth(id, Math.max(startWidth + delta, minWidth));
    };

    const stopColumnResize = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', stopColumnResize);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    // eslint-disable-next-line i18next/no-literal-string -- CSS property value
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopColumnResize);
  };

  return (
    <TableHead
      ref={mergedRef}
      style={style}
      className={`group/col relative h-10 select-none border-t border-border-light type-label-sm text-muted-foreground transition-colors hover:bg-accent ${isDragging ? 'opacity-50' : ''}`}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            else if (e.key === 'Escape') cancelRename();
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="h-6 w-full rounded-md border border-border bg-background px-1.5 text-sm text-foreground outline-none focus-visible:border-primary"
        />
      ) : (
        <div className="group/header relative flex items-center">
          {sortable ? (
            <button
              type="button"
              aria-label={t('trackersBoard.trackersProjectGroup.sortBy', { label })}
              onClick={(e) => {
                e.stopPropagation();
                onToggleSort?.(id);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={`absolute left-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-background transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                sortDir
                  ? 'text-foreground opacity-100'
                  : 'text-muted-foreground opacity-0 group-hover/header:opacity-100'
              }`}
            >
              {sortDir === 'asc' ? (
                <ArrowUp className="size-3.5" aria-hidden />
              ) : sortDir === 'desc' ? (
                <ArrowDown className="size-3.5" aria-hidden />
              ) : (
                <ArrowDownUp className="size-3.5" aria-hidden />
              )}
            </button>
          ) : null}
          <div
            className={`w-full truncate text-center ${readOnly ? '' : 'cursor-grab'}`}
            onDoubleClick={(e) => {
              if (!canRename) return;
              e.stopPropagation();
              setEditing(true);
            }}
            title={
              canRename ? t('trackersBoard.trackersProjectGroup.doubleClickToRename') : undefined
            }
            {...(readOnly ? {} : { ...attributes, ...listeners })}
          >
            {label}
          </div>
          {readOnly || !manageable ? null : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('trackersBoard.trackersProjectGroup.columnMenu', { label })}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="absolute right-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded bg-background text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover/header:opacity-100 data-[state=open]:opacity-100"
                >
                  <MoreVertical className="size-3.5" aria-hidden />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                {/* Type-specific configuration appears first when applicable,
                 separated from the generic actions below by a divider. */}
                {configureLabelKey && menu.configure ? (
                  <>
                    <DropdownMenuItem onSelect={() => onConfigure(id)}>
                      <Settings className="size-4" aria-hidden />
                      {t(configureLabelKey)}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : null}
                {menu.rename ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      // Defer so the dropdown can close before the input mounts.
                      setTimeout(() => setEditing(true), 0);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden />
                    {t('trackersBoard.trackersProjectGroup.rename')}
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onSelect={() => onAddColumnAfter(id)}>
                  <PlusIcon className="size-4" aria-hidden />
                  {t('trackersBoard.trackersProjectGroup.addColumnToTheRight')}
                </DropdownMenuItem>
                {menu.duplicate ? (
                  <DropdownMenuItem onSelect={() => onDuplicate(id)}>
                    <Copy className="size-4" aria-hidden />
                    {t('trackersBoard.trackersProjectGroup.duplicate')}
                  </DropdownMenuItem>
                ) : null}
                {menu.remove ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => onDelete(id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="size-4" aria-hidden />
                      {t('trackersBoard.trackersProjectGroup.delete')}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}
      {readOnly ? null : <ResizeGrip onMouseDown={handleResizeMouseDown} />}
    </TableHead>
  );
}

interface TrackersProjectGroupProps {
  /** The group header: `id` drives group drag-reorder, `title` the label. */
  project: { id: string; title: string | null };
  /** Rows in this group (briefs or free-form tracker items). */
  items: BoardRowItem[];
  itemCount: number;
  /**
   * Rows in this group before search and filters narrowed them. An empty group
   * reads differently depending on which of the two it is, and `items` alone
   * cannot tell them apart. Required: neither default is right for both boards.
   */
  unfilteredItemCount: number;
  isExpanded: boolean;
  onToggle: () => void;
  colorIndex: number;
  /** Accent color override (custom groups); defaults to the palette by index. */
  color?: string;
  onReorderItems: (groupId: string, activeId: string, overId: string) => void;
  /**
   * When true, item rows are NOT wrapped in this group's own `DndContext`;
   * they rely on a single table-level context so items can be dragged
   * between groups. Enabled for custom trackers (see `onMoveItem` on the
   * table). Briefs keep the per-group context (in-group reorder only).
   */
  unifiedItemDnd?: boolean;
  /** Inline-rename the group's title (custom groups only). */
  onRenameGroup?: (groupId: string, nextLabel: string) => void;
  /** Delete the whole group (custom groups only). Items fall back to Ungrouped. */
  onDeleteGroup?: (groupId: string) => void;
  columnOrder: string[];
  columnWidths: Record<string, number>;
  onReorderColumns: (activeId: string, overId: string) => void;
  onSetColumnWidth: (columnId: string, width: number) => void;
  itemColumnWidth: number;
  onSetItemColumnWidth: (width: number) => void;
  scrollContainersRef: ScrollContainersRef;
  syncedScrollLeftRef: SyncedScrollLeftRef;
  /** Shared page scroll container; rows virtualize against it (see below). */
  boardScrollRef?: RefObject<HTMLDivElement | null>;
  /**
   * Changes whenever any group's expanded state or row count changes. Used only
   * to re-measure this group's virtualization offset when a sibling above it
   * grows/shrinks — cheaper and jank-free vs. observing content size on scroll.
   */
  layoutVersion?: string;
  tableMinContentWidth: number;
  onAddCustomColumn: (params: { type: CustomColumnType }) => void;
  onAddCustomColumnAfter: (afterColumnId: string, params: { type: CustomColumnType }) => void;
  onDuplicateColumn: (columnId: string) => void;
  onDeleteColumn: (columnId: string) => void;
  onConfigureColumn: (columnId: string) => void;
  /** Active column sort (drives the header arrow); undefined = none. */
  columnSort?: ColumnSort | null;
  onToggleColumnSort?: (columnId: string) => void;
  columnDefs: Record<string, { label: string; minWidth: number; type?: CustomColumnType }>;
  customColumns: CustomColumn[];
  onRenameColumn: (columnId: string, nextLabel: string) => void;
  /** Column types hidden from the Add-column picker (custom trackers exclude
   *  the brief-bound `brief_type` / `assets` types). */
  excludeColumnTypes?: readonly CustomColumnType[];
  // ── Row behavior (briefs default; free-form items opt into the rest) ──
  onItemClick?: (id: string) => void;
  /** Preview the row's file from its thumbnail cell — see `TrackersBriefRow`. */
  onOpenPreview?: (id: string) => void;
  /** The tracker these rows belong to — keys each row's comment thread. */
  trackerId?: string;
  onOpenComments?: (id: string) => void;
  /** Row whose comments panel is open — see `TrackersBriefRow`. */
  openCommentsRowKey?: string | null;
  showComments?: boolean;
  editableTitle?: boolean;
  onRenameItem?: (id: string, next: string) => void;
  onSetCellValue?: (itemId: string, columnId: string, value: unknown) => void;
  /** Which columns accept an edit — see `TrackersBriefRow`. */
  isColumnEditable?: (columnId: string, itemId: string) => boolean;
  /**
   * Which columns the board can order by. Defaults to whatever the column's
   * type can be compared on, which is the right answer for a board that sorts
   * its own rows and the wrong one for a board the server sorts.
   */
  isColumnSortable?: (columnId: string) => boolean;
  /** Which kebab actions each column honours; defaults to all of them. */
  columnMenu?: (columnId: string) => ColumnMenuActions;
  /** Rows and groups can be dragged into a new order — see `TrackersBriefRow`. */
  reorderable?: boolean;
  onDeleteItem?: (id: string) => void;
  /** Inline "add item" row at the bottom of the group (free-form items). */
  onAddItem?: (title: string) => void;
}

export function TrackersProjectGroup(props: TrackersProjectGroupProps) {
  const { t } = useTranslation('trackers');

  const {
    project,
    items,
    itemCount,
    unfilteredItemCount,
    isExpanded,
    onToggle,
    colorIndex,
    onReorderItems,
    unifiedItemDnd = false,
    onRenameGroup,
    onDeleteGroup,
    columnOrder,
    columnWidths,
    onReorderColumns,
    onSetColumnWidth,
    itemColumnWidth,
    onSetItemColumnWidth,
    boardScrollRef,
    layoutVersion,
    tableMinContentWidth,
    onAddCustomColumn,
    onAddCustomColumnAfter,
    onDuplicateColumn,
    onDeleteColumn,
    onConfigureColumn,
    columnSort,
    onToggleColumnSort,
    columnDefs,
    customColumns,
    onRenameColumn,
    excludeColumnTypes,
    onItemClick,
    onOpenPreview,
    trackerId,
    onOpenComments,
    openCommentsRowKey = null,
    showComments,
    editableTitle,
    onRenameItem,
    onSetCellValue,
    isColumnEditable,
    isColumnSortable,
    columnMenu,
    reorderable = true,
    onDeleteItem,
    onAddItem,
  } = props;

  const canEdit = useCanEditTracker();
  const canManageColumns = useCanManageTrackerColumns();
  const [addColumnModalOpen, setAddColumnModalOpen] = useState(false);
  /** When set, the AddColumnModal inserts after this column id. Null = append. */
  const [addColumnAfterId, setAddColumnAfterId] = useState<string | null>(null);
  const color = props.color ?? GROUP_COLORS[colorIndex % GROUP_COLORS.length];

  // Inline group-title rename (custom groups). Double-click the title → input.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(project.title ?? '');
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (editingTitle) {
      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }
  }, [editingTitle]);
  const commitTitle = () => {
    const next = titleDraft.trim();
    setEditingTitle(false);
    if (next && next !== project.title) onRenameGroup?.(project.id, next);
    else setTitleDraft(project.title ?? '');
  };

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: project.id,
  });

  const sortableStyle = {
    // `Translate.toString` emits only translate3d(...) — no scaleX/scaleY,
    // so the dragged element keeps its original width/height instead of
    // getting squashed or stretched by dnd-kit's default transform.
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const itemIds = useMemo(() => items.map((i) => i.id), [items]);

  // Computed once per group (not per row) and passed to every row — a per-row
  // useQuery here was one observer × every mounted row, a real scroll cost.
  const hasEnabledAutomations = useHasEnabledAutomations(trackerId ?? '');

  const columnSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const itemSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleColumnDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      onReorderColumns(String(active.id), String(over.id));
    },
    [onReorderColumns],
  );

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const handleItemDragStart = (event: DragStartEvent) => setActiveDragId(String(event.active.id));
  const handleItemDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    onReorderItems(project.id, String(active.id), String(over.id));
  };

  const itemCellRef = useRef<HTMLTableCellElement | null>(null);

  // The horizontal scroll wrapper is tagged with `data-trackers-hscroll="group"`.
  // Horizontal-scroll syncing across all groups + the bottom bar is wired
  // centrally in TrackersBoardTable (querying that attribute), which avoids the
  // per-component registration-timing races that broke sync for late-expanded
  // groups. `scrollContainersRef`/`syncedScrollLeftRef` are retained as props
  // for API compatibility but no longer used here.
  const scrollWrapperRef = useRef<HTMLDivElement | null>(null);

  // On-demand row virtualization for large groups. Rows window against the
  // shared page scroll container (`boardScrollRef`) rather than a per-group
  // inner scroller, so the whole board keeps a single natural vertical scrollbar
  // (monday.com's model). Small groups skip this entirely and render as before.
  //
  // Because the virtualized rows start partway down the shared scroller (below
  // this group's header and any groups above it), the virtualizer needs its
  // offset within that scroller as `scrollMargin`. We measure it in a layout
  // effect and re-measure whenever the scroller's content reflows.
  const shouldVirtualize = items.length > VIRTUALIZE_THRESHOLD;
  const [scrollMargin, setScrollMargin] = useState(0);

  // Measure this group's offset within the shared scroller (its `scrollMargin`).
  // The offset is scroll-position independent, so we DON'T observe content size
  // during scroll — doing so caused a reflow storm (the windowed rows' estimated
  // vs. real heights nudge the content box every frame). Instead we re-measure
  // only when layout that can shift the offset changes: this group's own
  // expand/row-count, a viewport resize, or `layoutVersion` (bumped by the board
  // whenever any group expands/collapses or its row count changes).
  useLayoutEffect(() => {
    const scroller = boardScrollRef?.current;
    const wrap = scrollWrapperRef.current;
    if (!scroller || !wrap || !shouldVirtualize) return;

    const remeasure = () => {
      const next =
        wrap.getBoundingClientRect().top -
        scroller.getBoundingClientRect().top +
        scroller.scrollTop;
      setScrollMargin((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };

    remeasure();
    // Viewport resize can shift the toolbar/header height above the rows. This
    // fires on the scroller's box size only — never on scroll.
    const ro = new ResizeObserver(remeasure);
    ro.observe(scroller);
    return () => ro.disconnect();
  }, [boardScrollRef, shouldVirtualize, isExpanded, items.length, layoutVersion]);

  const rowVirtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => boardScrollRef?.current ?? null,
    estimateSize: () => 48,
    overscan: 12,
    scrollMargin,
    enabled: shouldVirtualize,
  });

  const handleItemResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = itemCellRef.current?.getBoundingClientRect().width ?? itemColumnWidth;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const delta = moveEvent.clientX - startX;
        onSetItemColumnWidth(Math.max(startWidth + delta, ITEM_COLUMN_MIN_WIDTH));
      };

      const stopColumnResize = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', stopColumnResize);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      // eslint-disable-next-line i18next/no-literal-string -- CSS property value
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', stopColumnResize);
    },
    [itemColumnWidth, onSetItemColumnWidth],
  );

  // The column header (labels, resize grips, column DnD) doesn't depend on
  // scroll position, so we memoize it: without this it re-rendered on every
  // virtualizer tick during scroll (a fixed ~20ms/frame cost). Deps are all the
  // values it reads; during scroll none change, so React reuses this subtree.
  const columnHeader = useMemo(
    () => (
      <DndContext
        sensors={columnSensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        autoScroll={false}
        onDragEnd={handleColumnDragEnd}
      >
        <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
          <TableHeader>
            <TableRow className="hover:!bg-transparent has-aria-expanded:!bg-transparent data-[state=selected]:!bg-transparent">
              <TableHead
                ref={itemCellRef}
                className={`group/col sticky left-0 z-10 h-10 select-none border-t border-border-light bg-background type-label-sm text-muted-foreground transition-colors hover:bg-accent ${STICKY_EDGE_SHADOW}`}
                style={{
                  width: `${itemColumnWidth}px`,
                  minWidth: `${ITEM_COLUMN_MIN_WIDTH}px`,
                  // Align with the FileText icon's left edge in the rows
                  // below: pl-2 (8px) + icon centering offset (8px) = 16px.
                  paddingLeft: 16,
                }}
              >
                {/* The item source (Free form / Briefs) is fixed at
                    creation — no per-column configure menu here. */}
                <div className="truncate">{t('trackersBoard.trackersProjectGroup.item')}</div>
                {canEdit ? <ResizeGrip onMouseDown={handleItemResizeMouseDown} /> : null}
              </TableHead>
              {/* Fixed Comments column header — no label, no menu. Gated
                 by `showComments` so it stays in lockstep with the rows'
                 comments cell; otherwise (custom trackers) the columns
                 would shift one slot out of alignment under fixed layout. */}
              {showComments === false ? null : (
                <TableHead
                  className={`sticky z-10 h-10 select-none border-t border-border-light bg-background p-0 ${STICKY_EDGE_SHADOW}`}
                  style={{
                    left: `${itemColumnWidth}px`,
                    width: COMMENTS_COLUMN_WIDTH,
                    minWidth: COMMENTS_COLUMN_WIDTH,
                  }}
                />
              )}
              {columnOrder.map((colId) => {
                const def = columnDefs[colId];
                // `columnOrder` can briefly hold an id that isn't in
                // `columnDefs` yet (e.g. a just-added column before
                // `customColumns` settles); skip rather than crash on
                // `def.label`. The index type says this can't be
                // undefined, but at runtime it can.
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                if (!def) return null;
                return (
                  <SortableColumnHead
                    key={colId}
                    id={colId}
                    label={def.label}
                    width={columnWidths[colId]}
                    minWidth={def.minWidth}
                    configureLabelKey={def.type ? CONFIGURE_LABEL_KEY[def.type] : undefined}
                    onSetWidth={onSetColumnWidth}
                    onRename={onRenameColumn}
                    onAddColumnAfter={(afterId) => {
                      setAddColumnAfterId(afterId);
                      setAddColumnModalOpen(true);
                    }}
                    onDuplicate={onDuplicateColumn}
                    onDelete={onDeleteColumn}
                    onConfigure={onConfigureColumn}
                    menu={columnMenu?.(colId)}
                    sortable={isColumnSortable?.(colId) ?? isSortableColumnType(def.type)}
                    sortDir={columnSort?.columnId === colId ? columnSort.dir : null}
                    onToggleSort={onToggleColumnSort}
                    readOnly={!canEdit}
                    manageable={canManageColumns}
                  />
                );
              })}
              {/* + button: opens the AddColumnModal. Shown on every group's
                 header so the action stays close to the cursor wherever the
                 user is. The modal is shared via the parent callback. */}
              <TableHead
                className="h-10 border-t border-border-light bg-background p-0 text-center"
                style={{ width: 40, minWidth: 40 }}
              >
                {canManageColumns ? (
                  <button
                    type="button"
                    aria-label={t('trackersBoard.trackersProjectGroup.addColumn')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAddColumnModalOpen(true);
                    }}
                    className="flex h-full w-full items-center justify-center text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <PlusIcon className="size-4" aria-hidden />
                  </button>
                ) : null}
              </TableHead>
              {/* Spacer cell — fills remaining width; keeps row rules running edge-to-edge. */}
              <TableHead className="h-10 border-t border-border-light bg-background" />
            </TableRow>
          </TableHeader>
        </SortableContext>
      </DndContext>
    ),
    [
      columnSensors,
      handleColumnDragEnd,
      handleItemResizeMouseDown,
      columnOrder,
      columnDefs,
      columnWidths,
      itemColumnWidth,
      showComments,
      columnSort,
      columnMenu,
      isColumnSortable,
      onSetColumnWidth,
      onRenameColumn,
      onDuplicateColumn,
      onDeleteColumn,
      onConfigureColumn,
      onToggleColumnSort,
      canEdit,
      canManageColumns,
      setAddColumnAfterId,
      setAddColumnModalOpen,
      t,
    ],
  );

  return (
    <div
      ref={setNodeRef}
      style={{ ...sortableStyle, borderLeftColor: color }}
      className={`group overflow-hidden rounded-lg border border-l-[3px] border-border-light ${isDragging ? 'opacity-50' : ''}`}
    >
      {/*
        Header is a plain row (NOT an interactive element) so the collapse
        toggle, rename input, drag grip, and delete can be real, independently
        focusable controls — no interactive-inside-interactive nesting.
        `${color}14` = the accent hex at ~8% alpha — a faded wash of the group
        color over the card, beside the block's accent left edge.
      */}
      <div
        className="flex h-11 items-center gap-xs px-md type-body-md font-medium"
        style={{ backgroundColor: `${color}14` }}
      >
        {onRenameGroup && editingTitle ? (
          <>
            {isExpanded ? (
              <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
            )}
            <input
              ref={titleInputRef}
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={commitTitle}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitTitle();
                else if (e.key === 'Escape') {
                  setTitleDraft(project.title ?? '');
                  setEditingTitle(false);
                }
              }}
              style={{ color }}
              className="h-6 flex-1 rounded-md border border-border bg-background px-1.5 text-sm font-medium outline-none focus-visible:border-primary"
            />
          </>
        ) : (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isExpanded}
            className="flex min-w-0 flex-1 items-center gap-2 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {isExpanded ? (
              <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" />
            )}
            <span
              style={{ color }}
              className={onRenameGroup ? 'cursor-text' : undefined}
              title={
                onRenameGroup
                  ? t('trackersBoard.trackersProjectGroup.doubleClickToRename')
                  : undefined
              }
              onDoubleClick={
                onRenameGroup
                  ? (e) => {
                      e.stopPropagation();
                      setTitleDraft(project.title ?? '');
                      setEditingTitle(true);
                    }
                  : undefined
              }
            >
              {project.title ?? t('trackersBoard.trackersProjectGroup.untitledProject')}
            </span>
          </button>
        )}
        {reorderable ? (
          <button
            type="button"
            aria-label={t('trackersBoard.trackersProjectGroup.dragGroup', {
              title: project.title ?? t('trackersBoard.trackersProjectGroup.untitled'),
            })}
            className="cursor-grab text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            {...attributes}
            {...listeners}
          >
            <GripVerticalIcon className="size-4" aria-hidden />
          </button>
        ) : null}
        {/* Renaming was reachable only by double-clicking the title, which is a
            gesture with nothing on screen to suggest it. The button sits beside
            delete so the group header's actions are found the same way. */}
        {onRenameGroup && !editingTitle ? (
          <button
            type="button"
            aria-label={t('trackersBoard.trackersProjectGroup.renameGroup', {
              title: project.title ?? t('trackersBoard.trackersProjectGroup.untitled'),
            })}
            className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => {
              setTitleDraft(project.title ?? '');
              setEditingTitle(true);
            }}
          >
            <Pencil className="size-4" aria-hidden />
          </button>
        ) : null}
        {onDeleteGroup ? (
          <button
            type="button"
            aria-label={t('trackersBoard.trackersProjectGroup.deleteGroup', {
              title: project.title ?? t('trackersBoard.trackersProjectGroup.untitled'),
            })}
            className="rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-accent hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => onDeleteGroup(project.id)}
          >
            <Trash2 className="size-4" aria-hidden />
          </button>
        ) : null}
        <span className="ml-auto type-caption text-muted-foreground">
          {t('trackersBoard.trackersProjectGroup.itemCount', { count: itemCount })}
        </span>
      </div>

      {isExpanded && (
        <div
          ref={scrollWrapperRef}
          data-trackers-hscroll="group"
          className="group/hscroll overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        >
          <table
            className="caption-bottom border-separate border-spacing-0 text-sm"
            style={{
              tableLayout: 'fixed',
              width: `max(100%, ${tableMinContentWidth}px)`,
            }}
          >
            {columnHeader}
            {(() => {
              // `renderRow` is a thin 1:1 prop forward to
              // `TrackersBriefRow` — it closes over ~16 values
              // (onItemClick, columnOrder, columnWidths,
              // itemColumnWidth, columnDefs, customColumns, trackerId,
              // hasEnabledAutomations, onOpenComments, showComments,
              // editableTitle, onRenameItem, onSetCellValue, onDeleteItem,
              // unifiedItemDnd) and is called from 3 sites inside this same
              // windowing block (full render, virtualized window, and the
              // kept-mounted active-drag row). Extracting it to module scope
              // or a sibling file would require threading the exact same 16
              // props explicitly — no readability gain over the closure, so
              // it stays local.
              const renderRow = (item: BoardRowItem) => (
                <TrackersBriefRow
                  key={item.id}
                  item={item}
                  onClick={onItemClick}
                  onOpenPreview={onOpenPreview}
                  columnOrder={columnOrder}
                  columnWidths={columnWidths}
                  itemColumnWidth={itemColumnWidth}
                  columnDefs={columnDefs}
                  customColumns={customColumns}
                  trackerId={trackerId}
                  hasEnabledAutomations={hasEnabledAutomations}
                  onOpenComments={onOpenComments}
                  isCommentsPanelOpen={openCommentsRowKey === item.id}
                  showComments={showComments}
                  editableTitle={editableTitle}
                  onRename={onRenameItem}
                  onSetCellValue={onSetCellValue}
                  isColumnEditable={isColumnEditable}
                  reorderable={reorderable}
                  onDelete={onDeleteItem}
                  dragWithOverlay={unifiedItemDnd}
                />
              );

              // Windowing: reserve off-screen height with spacer <tr>s and mount
              // only the visible slice. `getVirtualItems()` is empty unless the
              // virtualizer is enabled + measured, so this falls back to the full
              // render for small groups and pre-measure paints.
              const virtualItems = rowVirtualizer.getVirtualItems();
              const useWindowing = shouldVirtualize && virtualItems.length > 0;
              // Generous colSpan for the spacer rows (item + comments + columns +
              // add-column + buffer); overshooting is harmless.
              const spacerCols = columnOrder.length + 3;

              let rows: React.ReactNode;
              if (!useWindowing) {
                rows = items.map(renderRow);
              } else {
                // `start`/`end` are measured from the scroller origin (they
                // include `scrollMargin`), while `getTotalSize()` is just this
                // group's list height — subtract the margin to get the spacer
                // heights *within* the group.
                const sm = rowVirtualizer.options.scrollMargin;
                const totalSize = rowVirtualizer.getTotalSize();
                const paddingTop = Math.max(0, virtualItems[0].start - sm);
                const paddingBottom = Math.max(
                  0,
                  totalSize - (virtualItems[virtualItems.length - 1].end - sm),
                );
                const windowIndexes = new Set(virtualItems.map((v) => v.index));
                // Keep the actively-dragged row mounted even if it scrolled out of
                // the window, so in-place (briefs) drag never loses its node.
                const activeIndex = activeDragId
                  ? items.findIndex((i) => i.id === activeDragId)
                  : -1;
                rows = (
                  <>
                    {paddingTop > 0 ? (
                      <tr aria-hidden>
                        <td colSpan={spacerCols} style={{ height: paddingTop }} />
                      </tr>
                    ) : null}
                    {virtualItems.map((v) => renderRow(items[v.index]))}
                    {activeIndex >= 0 && !windowIndexes.has(activeIndex)
                      ? renderRow(items[activeIndex])
                      : null}
                    {paddingBottom > 0 ? (
                      <tr aria-hidden>
                        <td colSpan={spacerCols} style={{ height: paddingBottom }} />
                      </tr>
                    ) : null}
                  </>
                );
              }

              const body = (
                <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
                  <TableBody>
                    {rows}
                    {items.length === 0 ? (
                      <TableRow className="hover:!bg-transparent">
                        <TableCell
                          colSpan={spacerCols}
                          className="h-12 border-t border-border-light bg-card px-4 text-sm text-muted-foreground"
                        >
                          {/* A group whose count is not zero has rows; they
                              are simply on a page the board has not reached.
                              Saying "no items match" there contradicts the
                              number in this group's own header. */}
                          {itemCount > 0
                            ? t('trackersBoard.trackersProjectGroup.scrollToLoadTheseItems')
                            : unfilteredItemCount === 0
                              ? t('trackersBoard.trackersProjectGroup.noItemsYet')
                              : t('trackersBoard.trackersProjectGroup.noItemsMatchTheCurrent')}
                        </TableCell>
                      </TableRow>
                    ) : null}
                    {onAddItem ? (
                      <AddItemRow
                        itemColumnWidth={itemColumnWidth}
                        restColSpan={spacerCols}
                        onAdd={onAddItem}
                      />
                    ) : null}
                  </TableBody>
                </SortableContext>
              );
              // Unified mode: rows join the single table-level DndContext so
              // they can be dragged across groups — no local context here.
              return unifiedItemDnd ? (
                body
              ) : (
                <DndContext
                  sensors={itemSensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToVerticalAxis]}
                  autoScroll={false}
                  onDragStart={handleItemDragStart}
                  onDragEnd={handleItemDragEnd}
                >
                  {body}
                </DndContext>
              );
            })()}
          </table>
        </div>
      )}
      <AddColumnModal
        open={addColumnModalOpen}
        excludeTypes={excludeColumnTypes}
        onOpenChange={(next) => {
          setAddColumnModalOpen(next);
          if (!next) setAddColumnAfterId(null);
        }}
        onAdd={(type) => {
          if (addColumnAfterId) onAddCustomColumnAfter(addColumnAfterId, { type });
          else onAddCustomColumn({ type });
          setAddColumnAfterId(null);
        }}
      />
    </div>
  );
}

/** Inline "add item" row pinned to the sticky Item column (free-form items). */
function AddItemRow({
  itemColumnWidth,
  restColSpan,
  onAdd,
}: {
  itemColumnWidth: number;
  /** Spans the cells after the Item column, so the row's rule runs the full width. */
  restColSpan: number;
  onAdd: (title: string) => void;
}) {
  const { t } = useTranslation('trackers');

  const [title, setTitle] = useState('');
  const commit = () => {
    const next = title.trim();
    if (!next) return;
    setTitle('');
    onAdd(next);
  };
  return (
    <TableRow className="hover:!bg-transparent">
      <TableCell
        className={`sticky left-0 z-10 h-11 border-t border-border-light bg-background py-0 ${STICKY_EDGE_SHADOW}`}
        style={{ width: `${itemColumnWidth}px` }}
      >
        <div className="flex items-center gap-2 pl-2">
          <PlusIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          <input
            value={title}
            placeholder={t('trackersBoard.trackersProjectGroup.addItem')}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
            }}
            onBlur={commit}
            className="min-w-0 flex-1 bg-transparent py-1 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
      </TableCell>
      <TableCell colSpan={restColSpan} className="border-t border-border-light bg-background" />
    </TableRow>
  );
}
